import axios from 'axios'
import { toast } from 'sonner'
import { unwrapApiEnvelope } from '@/shared/lib/api-envelope'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { clearAuthToken, getAuthToken, setAuthToken } from '@/shared/lib/auth-token'

const api = axios.create({
  baseURL: buildApiUrl('/api'),
  // Token transport only: the SPA authenticates with the X-Session-Token
  // header, never cookies (Firebase strips every cookie but __session).
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Re-export the shared token store so existing `@/admin/lib/api` imports of the
// token helpers keep resolving.
export { getAuthToken, setAuthToken, clearAuthToken }

// ---------------------------------------------------------------------------
// Logout suppression flag (stops the 401 handler redirecting mid-logout)
// ---------------------------------------------------------------------------

let isLoggingOut = false

export function setLoggingOut(value: boolean) {
  isLoggingOut = value
}

// ---------------------------------------------------------------------------
// Request interceptor - attach the session token + org/partner context
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Active org/partner context (sessionStorage, per tab)
// ---------------------------------------------------------------------------

/**
 * Forget which org/partner this tab was acting as.
 *
 * These two keys become the `X-Active-User-*` headers below, and sessionStorage
 * is per TAB, not per page - so a full navigation (impersonating someone, or
 * releasing them) carries the previous person's selection into the new session.
 * For an impersonated dealer that is not cosmetic: the stale upstream org
 * header fails to resolve AND suppresses the backend's sole-partnership
 * auto-select (`Audience._sole_partnership_pair` bails whenever an explicit
 * org header is present), so they land with no partner context at all - zero
 * permissions, 403 on every list. Call this whenever the acting user changes.
 */
export function clearActiveOrgContext(): void {
  try {
    sessionStorage.removeItem('activeOrganisationUuid')
    sessionStorage.removeItem('activePartnerUuid')
  } catch {
    // sessionStorage blocked; nothing was persisted to clear.
  }
}

api.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers['X-Session-Token'] = token
  }

  const orgUuid = sessionStorage.getItem('activeOrganisationUuid')
  if (orgUuid) {
    config.headers['X-Active-User-Organisation-UUID'] = orgUuid
  }
  const partnerUuid = sessionStorage.getItem('activePartnerUuid')
  if (partnerUuid) {
    config.headers['X-Active-User-Partner-UUID'] = partnerUuid
  }
  // FormData (file uploads: bulk import, attachments, images) MUST be sent as
  // multipart so the browser adds the boundary and the server's multipart
  // parser runs. The instance default `Content-Type: application/json` would
  // otherwise ride along on the FormData body, the server would skip multipart
  // parsing, and every upload would fail with "A file is required". Drop it and
  // let the browser set `multipart/form-data; boundary=...`.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

// ---------------------------------------------------------------------------
// Response interceptor - unwrap the business envelope + handle 401
// ---------------------------------------------------------------------------

api.interceptors.response.use(
  (response) => unwrapApiEnvelope(response),
  async (error) => {
    if (axios.isAxiosError(error) && error.response) {
      const { status } = error.response

      if (status === 401 && !isLoggingOut) {
        // The session token is gone, expired, or server-side revoked. Drop it
        // and bounce to login (unless already on a login/public page).
        const path = window.location.pathname
        if (
          !path.includes('/login') &&
          !path.includes('/partnership-invite') &&
          !path.includes('/forgot-password') &&
          !path.includes('/sso/callback')
        ) {
          toast.error('Your session has expired. Please log in again.')
          clearAuthToken()
          const returnTo = `${window.location.pathname}${window.location.search}`
          const next = returnTo && returnTo !== '/' ? `?next=${encodeURIComponent(returnTo)}` : ''
          if (path.startsWith('/staff')) {
            window.location.href = `/staff/login${next}`
          } else {
            const slug = sessionStorage.getItem('tenantSlug')
            window.location.href = slug ? `/${slug}/login${next}` : `/login${next}`
          }
        }
      }
      // 403 - silently reject; components handle permission errors themselves.
    }

    return Promise.reject(error)
  },
)

export default api
